import { ColDef } from 'ag-grid-enterprise';
import {
  AG_GRID_FILTER_BIGINT,
  AG_GRID_FILTER_DATE_TIME,
  AG_GRID_FILTER_DECIMAL,
  AG_GRID_FILTER_SELECT,
  AG_GRID_FILTER_SELECT_MULTIPLE,
  AG_GRID_FILTER_STUB_BIGINT,
  AG_GRID_FILTER_STUB_DATE_TIME,
  AG_GRID_FILTER_STUB_DECIMAL,
  AG_GRID_FILTER_STUB_SELECT,
  AG_GRID_FILTER_STUB_SELECT_MULTIPLE,
  AG_GRID_FILTER_STUB_TEXT,
  AG_GRID_FILTER_TEXT,
} from './components';
import { SelectValues } from '@/types/select';

// TFilterValue does not necessarily match TValue, see ag-table/columns/marketplace.ts::status
export function filterSelect<TData, TValue, TFilterValue>(
  values: SelectValues<TFilterValue>
): ColDef<TData, TValue> {
  return {
    filter: AG_GRID_FILTER_STUB_SELECT,
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    floatingFilterComponent: AG_GRID_FILTER_SELECT,
    floatingFilterComponentParams: { values },
  };
}

export function filterSelectMultiple<TData, TValue>(config: {
  values: SelectValues<TValue>;
  noSelectAll?: boolean;
  noSearchBox?: boolean;
}): ColDef<TData, TValue> {
  return {
    filter: AG_GRID_FILTER_STUB_SELECT_MULTIPLE,
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    floatingFilterComponent: AG_GRID_FILTER_SELECT_MULTIPLE,
    floatingFilterComponentParams: {
      values: config.values,
      noSelectAll: config.noSelectAll,
      noSearchBox: config.noSearchBox,
    },
  };
}

export function filterDate<TData, TValue>(): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_DATE_TIME,
    floatingFilterComponent: AG_GRID_FILTER_DATE_TIME,
    filterParams: {
      inputType: 'date',
    },
  };
}

export function filterTime<TData, TValue>(): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_DATE_TIME,
    floatingFilterComponent: AG_GRID_FILTER_DATE_TIME,
    filterParams: {
      inputType: 'time',
    },
  };
}

export function filterDateTime<TData, TValue>(): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_DATE_TIME,
    floatingFilterComponent: AG_GRID_FILTER_DATE_TIME,
    filterParams: {
      inputType: 'datetime-local',
    },
  };
}

export function filterText<TData, TValue>(config?: {
  trim?: boolean;
  mode?: 'equals' | 'contains';
  uppercase?: boolean;
  lowercase?: boolean;
  replace?: { searchValue: string | RegExp; replaceValue: string };
}): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_TEXT,
    floatingFilterComponent: AG_GRID_FILTER_TEXT,
    filterParams: {
      maxNumConditions: 1,
      debounceMs: 300,
      ...config,
    },
  };
}

export function filterDecimal<TData, TValue>(config?: {
  allowNegative?: boolean;
  valueConverter?: { get(value: string): string; set(value: string): string };
}): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_DECIMAL,
    floatingFilterComponent: AG_GRID_FILTER_DECIMAL,
    filterParams: {
      maxNumConditions: 1,
      debounceMs: 300,
      ...config,
    },
  };
}

export function filterBigInt<TData, TValue>(config?: {
  allowNegative?: boolean;
}): ColDef<TData, TValue> {
  return {
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    filter: AG_GRID_FILTER_STUB_BIGINT,
    floatingFilterComponent: AG_GRID_FILTER_BIGINT,
    filterParams: {
      maxNumConditions: 1,
      allowedCharPattern: '\\d',
      debounceMs: 300,
      numberParser: (text: string | null) => {
        if (!text) return null;
        return Number(text.replace(/,/g, ''));
      },
      ...config,
    },
  };
}
